import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Alert, Badge, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ShortlistStudents = ({ companyName, companyId, students, onShortlistUpdate }) => {
  const [shortlistedStudents, setShortlistedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabKey, setTabKey] = useState('manual');
  const [fileContent, setFileContent] = useState('');

  useEffect(() => {
    // Initialize with current shortlisted students
    const currentShortlisted = students
      .filter(student => 
        student.company_preferences?.some(p => 
          p.company_name === companyName && p.is_shortlisted
        )
      )
      .map(student => student.email);
    
    setShortlistedStudents(currentShortlisted);
    setError(''); // Clear any errors when company changes
  }, [companyName, students]);

  const handleToggleStudent = (email) => {
    if (shortlistedStudents.includes(email)) {
      setShortlistedStudents(shortlistedStudents.filter(e => e !== email));
    } else {
      setShortlistedStudents([...shortlistedStudents, email]);
    }
  };

  const handleSelectAll = () => {
    const allEmails = students.map(student => student.email);
    setShortlistedStudents(allEmails);
  };

  const handleClearAll = () => {
    setShortlistedStudents([]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setFileContent(content);
      
      // Detect file type and parse accordingly
      if (file.name.endsWith('.csv')) {
        parseCSVFile(content);
      } else if (file.name.endsWith('.xlsx')) {
        parseExcelFile(content);
      } else if (file.name.endsWith('.json')) {
        parseJSONFile(content);
      } else {
        // Assume it's a text file with one email per line
        parseTextFile(content);
      }
    };
    
    if (file.name.endsWith('.xlsx')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const parseCSVFile = (content) => {
    Papa.parse(content, {
      complete: (results) => {
        const emails = extractEmailsFromParsedData(results.data);
        setShortlistedStudents(emails);
      },
      error: (error) => {
        setError(`Error parsing CSV file: ${error.message}`);
      }
    });
  };

  const parseExcelFile = (content) => {
    try {
      const data = new Uint8Array(content);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsedData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      const emails = extractEmailsFromParsedData(parsedData);
      setShortlistedStudents(emails);
    } catch (error) {
      setError(`Error parsing Excel file: ${error.message}`);
    }
  };

  const parseJSONFile = (content) => {
    try {
      const data = JSON.parse(content);
      
      // Check if the JSON follows our API format
      if (data.shortlisted_student_emails && Array.isArray(data.shortlisted_student_emails)) {
        setShortlistedStudents(data.shortlisted_student_emails);
      } else {
        // Otherwise, just extract any emails we can find
        const emails = extractEmailsFromJSONValues(data);
        setShortlistedStudents(emails);
      }
    } catch (error) {
      setError(`Error parsing JSON file: ${error.message}`);
    }
  };

  const parseTextFile = (content) => {
    const lines = content.split(/[\r\n]+/);
    const emails = lines
      .map(line => line.trim())
      .filter(line => line.includes('@') && line.length > 0);
    
    setShortlistedStudents(emails);
  };

  const extractEmailsFromParsedData = (data) => {
    // Flatten the array and extract anything that looks like an email
    const flatData = data.flat();
    return flatData
      .filter(item => item && typeof item === 'string' && item.includes('@'))
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const extractEmailsFromJSONValues = (obj) => {
    const emails = [];
    
    const traverse = (o) => {
      if (!o) return;
      
      if (Array.isArray(o)) {
        o.forEach(item => traverse(item));
      } else if (typeof o === 'object') {
        Object.values(o).forEach(value => traverse(value));
      } else if (typeof o === 'string' && o.includes('@')) {
        emails.push(o.trim());
      }
    };
    
    traverse(obj);
    return emails;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
  
      // Use the passed company ID
      if (!companyId) {
        setError('Company ID not found. Please try again.');
        return;
      }
  
      // Create payload as a list containing one object
      const payload = [
        {
          company_id: companyId,
          shortlisted_student_emails: shortlistedStudents
        }
      ];
  
      console.log('Shortlist Update Payload:', payload);
  
      // Call the parent handler with the correctly formatted payload
      await onShortlistUpdate(payload);
  
      toast.success(`Shortlist updated for ${companyName}`);
    } catch (error) {
      console.error('Error updating shortlist:', error);
      setError(error.message || 'Failed to update shortlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = () => {
    // Create simple CSV with current shortlisted students
    const header = "shortlisted_student_emails\n";
    const content = shortlistedStudents.join('\n');
    const csv = header + content;
    
    // Create and download the file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${companyName.replace(/\s+/g, '_')}_shortlist_template.csv`;
    link.click();
  };

  const downloadJSONTemplate = () => {
    // Use the passed company ID
    
    // Create JSON template matching the API format
    const jsonData = [{
      "company_id": companyId,
      "shortlisted_student_emails": shortlistedStudents.length > 0 ? 
        shortlistedStudents : 
        ["example1@pilani.bits-pilani.ac.in", "example2@pilani.bits-pilani.ac.in"]
    }];
    
    // Create and download the file
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${companyName.replace(/\s+/g, '_')}_shortlist_template.json`;
    link.click();
  };

  // Sort students by preference order (if they have preferences), otherwise by email
  const sortedStudents = [...students].sort((a, b) => {
    const prefA = a.company_preferences?.find(p => p.company_name === companyName)?.preference_order;
    const prefB = b.company_preferences?.find(p => p.company_name === companyName)?.preference_order;
    
    // If both have preferences, sort by preference order
    if (prefA !== undefined && prefB !== undefined) {
      return prefA - prefB;
    }
    // If only one has preference, put it first
    if (prefA !== undefined) return -1;
    if (prefB !== undefined) return 1;
    // If neither has preference, sort by email
    return a.email.localeCompare(b.email);
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>{companyName} - Student Preferences</h5>
        <div>
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="me-2"
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Tabs
        activeKey={tabKey}
        onSelect={(k) => setTabKey(k)}
        className="mb-3"
      >
        <Tab eventKey="manual" title="Manual Selection">
          {students.length === 0 ? (
            <Alert variant="info">No students found in the system.</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th width="60">Shortlist</th>
                    <th>Student</th>
                    <th width="100">Preference</th>
                    <th width="120">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((student) => {
                    const preference = student.company_preferences?.find(
                      p => p.company_name === companyName
                    );
                    
                    return (
                      <tr key={student.email}>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={shortlistedStudents.includes(student.email)}
                            onChange={() => handleToggleStudent(student.email)}
                          />
                        </td>
                        <td>
                          <div>{student.name || student.user?.first_name || student.email.split('@')[0]}</div>
                          <div className="text-muted small">{student.email}</div>
                        </td>
                        <td className="text-center">
                          {preference ? (
                            <Badge bg="info">
                              {preference.preference_order}
                            </Badge>
                          ) : (
                            <Badge bg="secondary">No Pref</Badge>
                          )}
                        </td>
                        <td>
                          {preference?.is_shortlisted ? (
                            <Badge bg="success">Shortlisted</Badge>
                          ) : shortlistedStudents.includes(student.email) ? (
                            <Badge bg="warning">To be Shortlisted</Badge>
                          ) : (
                            <Badge bg="secondary">Not Shortlisted</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Tab>
        <Tab eventKey="file" title="File Upload">
          <Form.Group className="mb-3">
            <Form.Label>Upload student emails (CSV, Excel, JSON, or Text file)</Form.Label>
            <Form.Control
              type="file"
              accept=".txt,.csv,.xlsx,.json"
              onChange={handleFileChange}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              File should contain student emails to shortlist
              <Button 
                variant="link" 
                className="p-0 ms-1" 
                onClick={downloadCSVTemplate}
                disabled={loading}
              >
                Download CSV template
              </Button>
              {' | '}
              <Button 
                variant="link" 
                className="p-0" 
                onClick={downloadJSONTemplate}
                disabled={loading}
              >
                Download JSON template
              </Button>
            </Form.Text>
          </Form.Group>
          
          {fileContent && (
            <div className="mt-3">
              <h6>Selected Students ({shortlistedStudents.length})</h6>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <ul className="list-unstyled mb-0">
                  {shortlistedStudents.map(email => (
                    <li key={email} className="small">
                      {email}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Tab>
      </Tabs>
      
      <div className="text-end mt-3">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Updating...
            </>
          ) : `Update Shortlist (${shortlistedStudents.length} students)`}
        </Button>
      </div>
    </div>
  );
};

export default ShortlistStudents;